import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useProject } from '../contexts/ProjectContext'
import { PROJECT_IDS } from '../config/projects'
import '../styles/Sidebar.css'

const Sidebar = ({ isMobileOpen = false, onCloseMobile = () => {}, onOpenProjectsModal = () => {} }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { selectProject, projects, selectedProject, projectPendingCounts } = useProject()

  const handleAllProjectsClick = (e) => {
    e.preventDefault()
    onOpenProjectsModal()
  }

  // Collapsible section states
  const [approvalsOpen, setApprovalsOpen] = useState(true)
  const [projectsOpen, setProjectsOpen] = useState(false)
  const [applicationsOpen, setApplicationsOpen] = useState(false)

  // Helper function to check if a route is active
  const isActive = (path) => {
    return location.pathname === path
  }

  // Check if Equipment Yard is active
  const isEquipmentYardActive = () => {
    return location.pathname === '/dashboard' && selectedProject?.id === PROJECT_IDS.EQUIPMENT_YARD
  }

  // Check if Cost Module is active
  const isCostModuleActive = () => {
    return location.pathname === '/dashboard' && selectedProject?.id === PROJECT_IDS.COST_MODULE
  }

  // Handle navigation to Equipment Yard
  const handleEquipmentYardClick = (e) => {
    e.preventDefault()
    const equipmentYardProject = projects.find(p => p.id === PROJECT_IDS.EQUIPMENT_YARD)
    if (equipmentYardProject) {
      selectProject(equipmentYardProject)
      navigate('/dashboard')
    }
    onCloseMobile()
  }

  // Handle navigation to Cost Module (All Projects except Equipment Yard)
  const handleCostModuleClick = (e) => {
    e.preventDefault()
    selectProject({ id: PROJECT_IDS.COST_MODULE, name: 'Cost Module' })
    navigate('/dashboard')
    onCloseMobile()
  }

  // Calculate total projects count
  const totalProjectsCount = projects.length

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="sidebar-mobile-overlay" onClick={onCloseMobile}></div>
      )}

      <aside className={`sidebar ${isMobileOpen ? 'sidebar-mobile-open' : ''}`}>
        {/* Mobile Close Button */}
        <button className="sidebar-mobile-close" onClick={onCloseMobile}>
          ✕
        </button>

        {/* Navigation Menu */}
        <nav className="sidebar-nav">
        {/* Dashboard */}
        <Link
          to="/dashboard"
          className={`sidebar-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
        >
          <span className="sidebar-icon">🏠</span>
          <span className="sidebar-label">Dashboard</span>
        </Link>

        {/* Approvals Section */}
        <div className="sidebar-section">
          <button
            className="sidebar-section-header"
            onClick={() => setApprovalsOpen(!approvalsOpen)}
          >
            <span className="sidebar-icon">📋</span>
            <span className="sidebar-section-title">Approvals</span>
            <span className={`sidebar-chevron ${approvalsOpen ? 'open' : ''}`}>›</span>
          </button>

          {approvalsOpen && (
            <div className="sidebar-subsection">
              <a
                href="#"
                onClick={handleEquipmentYardClick}
                className={`sidebar-nav-item sidebar-subitem ${isEquipmentYardActive() ? 'active' : ''}`}
              >
                <span className="sidebar-icon">⛽</span>
                <span className="sidebar-label">Equipment Yard</span>
              </a>

              <a
                href="#"
                onClick={handleCostModuleClick}
                className={`sidebar-nav-item sidebar-subitem ${isCostModuleActive() ? 'active' : ''}`}
              >
                <span className="sidebar-icon">💰</span>
                <span className="sidebar-label">Cost Management</span>
              </a>
            </div>
          )}
        </div>

        {/* Projects Section - Hidden on Desktop */}
        <div className="sidebar-section sidebar-projects-section">
          <button
            className="sidebar-section-header"
            onClick={() => setProjectsOpen(!projectsOpen)}
          >
            <span className="sidebar-icon">📁</span>
            <span className="sidebar-section-title">Projects</span>
            <span className={`sidebar-chevron ${projectsOpen ? 'open' : ''}`}>›</span>
          </button>

          {projectsOpen && (
            <div className="sidebar-subsection">
              {/* Individual Project Links */}
              {projects.map(project => {
                const pendingCount = projectPendingCounts[project.id] || 0
                const isSelected = selectedProject?.id === project.id

                return (
                  <a
                    key={project.id}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      selectProject(project)
                      onCloseMobile()
                    }}
                    className={`sidebar-nav-item sidebar-subitem ${isSelected ? 'active' : ''}`}
                  >
                    <span className="sidebar-label">{project.name}</span>
                    {pendingCount > 0 && (
                      <span className="sidebar-count">{pendingCount}</span>
                    )}
                  </a>
                )
              })}

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="sidebar-nav-item sidebar-subitem"
              >
                <span className="sidebar-icon">⭐</span>
                <span className="sidebar-label">My Favorites</span>
              </a>
            </div>
          )}
        </div>

        {/* Applications Section */}
        <div className="sidebar-section">
          <button
            className="sidebar-section-header"
            onClick={() => setApplicationsOpen(!applicationsOpen)}
          >
            <span className="sidebar-icon">🔗</span>
            <span className="sidebar-section-title">Applications</span>
            <span className={`sidebar-chevron ${applicationsOpen ? 'open' : ''}`}>›</span>
          </button>

          {applicationsOpen && (
            <div className="sidebar-subsection">  
              <a
                href="https://acc.hdcc.com/vco"
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-nav-item sidebar-subitem sidebar-external-link"
              >
                <span className="sidebar-icon">🛡️</span>
                <span className="sidebar-label-group">
                  <span className="sidebar-label">VCO</span>
                  <span className="sidebar-sublabel">Vehicle Charge Out</span>
                </span>
              </a>

              <a
                href="https://acc.hdcc.com/vmr"
                target="_blank"
                rel="noopener noreferrer"
                className="sidebar-nav-item sidebar-subitem sidebar-external-link"
              >
                <span className="sidebar-icon">🔧</span>
                <span className="sidebar-label-group">
                  <span className="sidebar-label">VMR</span>
                  <span className="sidebar-sublabel">Vehicle Maintenance & Repair</span>
                </span>
              </a>
            </div>
          )}
        </div>
      </nav>
    </aside>
    </>
  )
}

export default Sidebar
